import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SecurityIcon from '@mui/icons-material/Security';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import apiClient from '../../api/client';
import { useAuth } from '../../contexts/AuthProvider';

const MfaSettings = ({ onNotification }) => {
  // Organization-level MFA settings (admin only)
  const [mfaRequired, setMfaRequired] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoadingOrgSettings, setIsLoadingOrgSettings] = useState(true);
  const [isSavingOrgSettings, setIsSavingOrgSettings] = useState(false);

  // User-level MFA factors
  const [enrolledFactors, setEnrolledFactors] = useState([]);
  const [isLoadingFactors, setIsLoadingFactors] = useState(true);

  // Enrollment dialogs
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [showTotpDialog, setShowTotpDialog] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [totpQrCode, setTotpQrCode] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);
  const [totpRegistrationId, setTotpRegistrationId] = useState(null);
  const [smsSessionToken, setSmsSessionToken] = useState(null);
  const [smsSessionJwt, setSmsSessionJwt] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(false);

  const { user, stytchSessionToken, stytchSessionJwt } = useAuth();

  // Load organization MFA settings (admin only)
  useEffect(() => {
    loadOrganizationSettings();
  }, []);

  // Load user's enrolled MFA factors
  useEffect(() => {
    loadEnrolledFactors();
  }, []);

  const loadOrganizationSettings = async () => {
    try {
      setIsLoadingOrgSettings(true);
      const response = await apiClient.get('/auth/mfa/organization-settings');

      if (response.data.success) {
        setMfaRequired(response.data.mfa_required || false);
        setIsAdmin(response.data.is_admin || false);
      }
    } catch (error) {
      console.error('Error loading MFA organization settings:', error);
      // User might not be admin or endpoint not available yet
      setIsAdmin(false);
    } finally {
      setIsLoadingOrgSettings(false);
    }
  };

  const loadEnrolledFactors = async () => {
    try {
      setIsLoadingFactors(true);
      const response = await apiClient.get('/auth/mfa/enrolled-factors');

      if (response.data.success) {
        setEnrolledFactors(response.data.factors || []);
      }
    } catch (error) {
      console.error('Error loading enrolled MFA factors:', error);
      setEnrolledFactors([]);
    } finally {
      setIsLoadingFactors(false);
    }
  };

  const handleToggleOrgMfa = async (enabled) => {
    try {
      setIsSavingOrgSettings(true);
      const response = await apiClient.post('/auth/mfa/organization-settings', {
        mfa_required: enabled
      });

      if (response.data.success) {
        setMfaRequired(enabled);
        onNotification?.({
          message: enabled
            ? 'MFA is now required for all users in your organization'
            : 'MFA is now optional for users in your organization',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error updating organization MFA settings:', error);
      onNotification?.({
        message: error.response?.data?.detail || 'Failed to update MFA settings',
        severity: 'error'
      });
      // Revert the toggle
      setMfaRequired(!enabled);
    } finally {
      setIsSavingOrgSettings(false);
    }
  };

  const handleStartSmsEnrollment = () => {
    setPhoneNumber('');
    setVerificationCode('');
    setSmsSessionToken(null);
    setSmsSessionJwt(null);
    setShowSmsDialog(true);
  };

  const handleStartTotpEnrollment = async () => {
    try {
      setIsEnrolling(true);
      const response = await apiClient.post('/auth/mfa/totp/enroll');

      if (response.data.success) {
        setTotpQrCode(response.data.qr_code);
        setTotpSecret(response.data.secret);
        setTotpRegistrationId(response.data.totp_registration_id || null);
        setVerificationCode('');
        setShowTotpDialog(true);
      } else {
        onNotification?.({
          message: 'Failed to start TOTP enrollment',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error starting TOTP enrollment:', error);
      onNotification?.({
        message: error.response?.data?.detail || 'Failed to start TOTP enrollment',
        severity: 'error'
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleCompleteSmsEnrollment = async () => {
    try {
      setIsEnrolling(true);

      // First, send verification code
      const sendResponse = await apiClient.post('/auth/mfa/sms/send', {
        phone_number: phoneNumber,
        session_token: stytchSessionToken,
        session_jwt: stytchSessionJwt
      });

      if (!sendResponse.data.success) {
        throw new Error('Failed to send verification code');
      }

      setSmsSessionToken(sendResponse.data.session_token || stytchSessionToken || null);
      setSmsSessionJwt(sendResponse.data.session_jwt || stytchSessionJwt || null);

      onNotification?.({
        message: 'Verification code sent to your phone',
        severity: 'info'
      });

      // Now wait for user to enter code and verify
      // This will be handled by a separate verify button
    } catch (error) {
      console.error('Error sending SMS code:', error);
      onNotification?.({
        message: error.response?.data?.detail || 'Failed to send verification code',
        severity: 'error'
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleVerifySmsEnrollment = async () => {
    try {
      setIsEnrolling(true);
      const response = await apiClient.post('/auth/mfa/sms/verify', {
        phone_number: phoneNumber,
        code: verificationCode,
        session_token: smsSessionToken || stytchSessionToken,
        session_jwt: smsSessionJwt || stytchSessionJwt
      });

      if (response.data.success) {
        onNotification?.({
          message: 'SMS authentication enabled successfully',
          severity: 'success'
        });
        setShowSmsDialog(false);
        setSmsSessionToken(null);
        setSmsSessionJwt(null);
        loadEnrolledFactors();
      } else {
        onNotification?.({
          message: 'Invalid verification code',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error verifying SMS enrollment:', error);
      onNotification?.({
        message: error.response?.data?.detail || 'Failed to verify code',
        severity: 'error'
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleCompleteTotpEnrollment = async () => {
    try {
      setIsEnrolling(true);
      const response = await apiClient.post('/auth/mfa/totp/verify', {
        code: verificationCode,
        secret: totpSecret,
        totp_registration_id: totpRegistrationId
      });

      if (response.data.success) {
        onNotification?.({
          message: 'Authenticator app enabled successfully',
          severity: 'success'
        });
        setShowTotpDialog(false);
        setTotpRegistrationId(null);
        loadEnrolledFactors();
      } else {
        onNotification?.({
          message: 'Invalid verification code',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error verifying TOTP enrollment:', error);
      onNotification?.({
        message: error.response?.data?.detail || 'Failed to verify code',
        severity: 'error'
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleRemoveFactor = async (factorId, factorType) => {
    if (!window.confirm(`Are you sure you want to remove ${factorType === 'sms' ? 'SMS' : 'authenticator app'} authentication?`)) {
      return;
    }

    try {
      const response = await apiClient.delete(`/auth/mfa/factor/${factorId}`);

      if (response.data.success) {
        onNotification?.({
          message: 'MFA factor removed successfully',
          severity: 'success'
        });
        loadEnrolledFactors();
      }
    } catch (error) {
      console.error('Error removing MFA factor:', error);
      onNotification?.({
        message: error.response?.data?.detail || 'Failed to remove MFA factor',
        severity: 'error'
      });
    }
  };

  if (isLoadingOrgSettings || isLoadingFactors) {
    return (
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        <SecurityIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        Multi-Factor Authentication (MFA)
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Organization-level settings (Admin only) */}
      {isAdmin && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            Organization Settings
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            As an administrator, you can require all users in your organization to use MFA for enhanced security.
          </Alert>
          <FormControlLabel
            control={
              <Switch
                checked={mfaRequired}
                onChange={(e) => handleToggleOrgMfa(e.target.checked)}
                disabled={isSavingOrgSettings}
              />
            }
            label={mfaRequired ? "MFA Required for All Users" : "MFA Optional for Users"}
          />
          {isSavingOrgSettings && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </Box>
      )}

      {/* User-level factor management */}
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Your Authentication Methods
        </Typography>
        <Alert severity={enrolledFactors.length > 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
          {enrolledFactors.length > 0
            ? `You have ${enrolledFactors.length} authentication method(s) enabled. Your account is more secure!`
            : mfaRequired
              ? 'MFA is required for your account. Please add at least one authentication method.'
              : 'Add an authentication method to enhance your account security.'}
        </Alert>

        {/* List of enrolled factors */}
        {enrolledFactors.length > 0 && (
          <List sx={{ mb: 2 }}>
            {enrolledFactors.map((factor) => (
              <ListItem key={factor.id} divider>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {factor.type === 'sms' ? (
                        <>
                          <PhoneAndroidIcon sx={{ mr: 1 }} />
                          SMS Authentication
                        </>
                      ) : (
                        <>
                          <QrCode2Icon sx={{ mr: 1 }} />
                          Authenticator App
                        </>
                      )}
                      <Chip label="Active" color="success" size="small" sx={{ ml: 2 }} />
                    </Box>
                  }
                  secondary={factor.type === 'sms' ? `Phone: ${factor.phone_masked}` : 'Google Authenticator, Authy, etc.'}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveFactor(factor.id, factor.type)}
                    disabled={mfaRequired && enrolledFactors.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {/* Add new factor buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PhoneAndroidIcon />}
            onClick={handleStartSmsEnrollment}
            disabled={enrolledFactors.some(f => f.type === 'sms')}
          >
            Add SMS Authentication
          </Button>
          <Button
            variant="outlined"
            startIcon={<QrCode2Icon />}
            onClick={handleStartTotpEnrollment}
            disabled={isEnrolling || enrolledFactors.some(f => f.type === 'totp')}
          >
            Add Authenticator App
          </Button>
        </Box>
      </Box>

      {/* SMS Enrollment Dialog */}
      <Dialog open={showSmsDialog} onClose={() => !isEnrolling && setShowSmsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add SMS Authentication</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter your phone number. We'll send you a verification code to confirm.
          </Alert>
          <TextField
            fullWidth
            label="Phone Number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+1234567890"
            sx={{ mb: 2 }}
            disabled={isEnrolling}
          />
          {phoneNumber && (
            <>
              <Button
                fullWidth
                variant="contained"
                onClick={handleCompleteSmsEnrollment}
                disabled={isEnrolling || !phoneNumber}
                sx={{ mb: 2 }}
              >
                Send Verification Code
              </Button>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                disabled={isEnrolling}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSmsDialog(false)} disabled={isEnrolling}>
            Cancel
          </Button>
          <Button
            onClick={handleVerifySmsEnrollment}
            disabled={isEnrolling || !verificationCode || verificationCode.length !== 6}
            variant="contained"
          >
            {isEnrolling ? <CircularProgress size={20} /> : 'Verify & Enable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TOTP Enrollment Dialog */}
      <Dialog open={showTotpDialog} onClose={() => !isEnrolling && setShowTotpDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Authenticator App</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.), then enter the code shown in the app.
          </Alert>
          {totpQrCode && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img src={totpQrCode} alt="QR Code" style={{ maxWidth: '250px' }} />
            </Box>
          )}
          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="Enter 6-digit code"
            disabled={isEnrolling}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTotpDialog(false)} disabled={isEnrolling}>
            Cancel
          </Button>
          <Button
            onClick={handleCompleteTotpEnrollment}
            disabled={isEnrolling || !verificationCode || verificationCode.length !== 6}
            variant="contained"
          >
            {isEnrolling ? <CircularProgress size={20} /> : 'Verify & Enable'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MfaSettings;
